import { Tabs as RadixTabs } from "radix-ui";
import type { ReactNode } from "react";

import { cn } from "./cn";

export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** A small count or marker after the label (e.g. pending invitations). */
  badge?: ReactNode;
  content: ReactNode;
}

/**
 * Sections of one page. Arrow keys move between tabs (Radix); the row scrolls sideways on narrow screens.
 * Controlled, so a page can keep the open tab in its address.
 */
export function Tabs<T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <RadixTabs.Root
      value={value}
      onValueChange={(next) => onChange(next as T)}
      className="flex flex-col gap-5"
    >
      <div className="-mx-1 [scrollbar-width:none] overflow-x-auto border-b border-border px-1 [&::-webkit-scrollbar]:hidden">
        <RadixTabs.List aria-label={label} className="flex gap-1">
          {items.map((item) => (
            <RadixTabs.Trigger
              key={item.value}
              value={item.value}
              className={cn(
                "-mb-px flex min-h-11 items-center gap-2 border-b-2 border-transparent px-3 text-sm font-semibold whitespace-nowrap text-muted transition-colors",
                "hover:text-ink data-[state=active]:border-primary data-[state=active]:text-ink",
              )}
            >
              {item.label}
              {item.badge}
            </RadixTabs.Trigger>
          ))}
        </RadixTabs.List>
      </div>
      {items.map((item) => (
        <RadixTabs.Content
          key={item.value}
          value={item.value}
          className="flex flex-col gap-4 focus-visible:outline-none"
        >
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
